import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, outlineButton, labelStyle, pillButton } from './adminTheme';
import { uploadToB2 } from '../utils/uploadToB2';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        heading: 'Fond de la page de connexion',
        active: '· actif',
        desc: 'Choisis une image en fond de la page /login — laisse désactivé pour le beige uni par défaut.',
        image: 'Image de fond',
        uploading: 'Upload en cours...',
        save: 'Sauvegarder',
        saving: 'Sauvegarde...',
        activate: 'Activer',
        loading: 'Chargement...',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        imageUploaded: 'Image uploadée',
        saveError: (msg) => `Erreur : ${msg}`,
        activated: 'Fond activé sur la page login',
        savedDisabled: 'Sauvegardé (désactivé)',
    },
    en: {
        heading: 'Login page background',
        active: '· active',
        desc: 'Choose a background image for the /login page — leave disabled for the default solid beige.',
        image: 'Background image',
        uploading: 'Uploading...',
        save: 'Save',
        saving: 'Saving...',
        activate: 'Activate',
        loading: 'Loading...',
        uploadError: (msg) => `Upload error: ${msg}`,
        imageUploaded: 'Image uploaded',
        saveError: (msg) => `Error: ${msg}`,
        activated: 'Background activated on the login page',
        savedDisabled: 'Saved (disabled)',
    },
};

function AdminLoginBg() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [form, setForm] = useState({ image_url: '', enabled: false });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchBg = async () => {
        setLoading(true);
        const { data } = await supabase.from('login_background').select('*').maybeSingle();
        if (data) setForm({ image_url: data.image_url || '', enabled: data.enabled || false });
        setLoading(false);
    };

    useEffect(() => { fetchBg(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fileName = `login-bg-${Date.now()}.${file.name.split('.').pop()}`;
            const url = await uploadToB2(`covers/${fileName}`, file);
            setForm(f => ({ ...f, image_url: url }));
            setStatusMsg(t.imageUploaded);
        } catch (err) {
            setStatusMsg(t.uploadError(err.message));
        }
        setUploading(false);
    };

    const handleSave = async (publish) => {
        setSaving(true);
        const payload = { image_url: form.image_url, enabled: publish };
        const { data: existing } = await supabase.from('login_background').select('*').limit(1).maybeSingle();

        let error;
        if (existing) ({ error } = await supabase.from('login_background').update(payload).eq('id', existing.id));
        else ({ error } = await supabase.from('login_background').insert(payload));

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setForm(f => ({ ...f, enabled: publish }));
            setStatusMsg(publish ? t.activated : t.savedDisabled);
        }
        setSaving(false);
    };

    if (loading) return <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>;

    return (
        <div style={{ maxWidth: '400px' }}>
            <p style={{ ...labelStyle, marginBottom: '4px' }}>
                {t.heading} {form.enabled && <span style={{ color: theme.textMuted }}>{t.active}</span>}
            </p>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.desc}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                    <label style={labelStyle}>{t.image}</label>
                    {form.image_url && (
                        <div style={{ width: '100%', aspectRatio: '16/9', marginBottom: '8px', background: theme.bg, overflow: 'hidden' }}>
                            <img src={form.image_url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                    {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="wlink" onClick={() => handleSave(false)} disabled={saving} style={outlineButton}>{t.save}</button>
                    <button className="wlink" onClick={() => handleSave(true)} disabled={saving} style={pillButton}>
                        {saving ? t.saving : t.activate}
                    </button>
                </div>

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
            </div>
        </div>
    );
}

export default AdminLoginBg;